import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'
import type { CategoryRule, RuleDecision, RuleScope } from '@domain/finance'
import type { CategoryRuleRepository } from '@domain/ports/category-rule-repository'
import { optionalText, requireText, textOr, toIso } from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * As regras de categorização, em `households/{hid}/categoryRules`.
 *
 * ## Uma leitura da coleção inteira, e sem índice
 *
 * São poucas centenas de documentos, e quem chama precisa de todos (ver a
 * porta). `getDocs` da coleção não usa índice composto nenhum — é a mesma
 * escolha da lista de categorias e da de estabelecimentos.
 *
 * ## O `upsert` procura pela CHAVE, e a chave é única
 *
 * Duas regras para `uber rides` seriam duas respostas para a mesma pergunta, e
 * a que ganha dependeria da ordem da leitura. Como a coleção é lida inteira de
 * qualquer jeito, procurar em memória custa uma leitura em vez de uma consulta.
 */
export function createFirebaseCategoryRuleRepository(db: Firestore): CategoryRuleRepository {
  const caminho = () => `households/${HOUSEHOLD_ID}/categoryRules`

  return {
    list: async () => {
      const snap = await getDocs(collection(db, caminho()))
      return snap.docs
        .map((d) => regraDe(d.id, d.data()))
        .sort((a, b) => b.appliedCount - a.appliedCount || a.label.localeCompare(b.label, 'pt-BR'))
    },

    upsert: async ({ key, label, decision, origin, scope }) => {
      // Chave vazia casaria com toda descrição que é só número. Ver
      // `regraQueCasa` no domínio.
      if (key === '') throw new Error('categoryRules: chave vazia não vira regra')

      const snap = await getDocs(collection(db, caminho()))
      const existente = snap.docs.find((d) => d.get('key') === key)

      if (existente !== undefined) {
        // `label` fica como nasceu: é a grafia que a pessoa reconhece na
        // tabela, e trocá-la a cada aplicação faria o nome mudar sozinho.
        await updateDoc(doc(db, caminho(), existente.id), {
          decision: decisionParaFirestore(decision),
          origin,
          scope,
          updatedAt: serverTimestamp(),
        })
        return existente.id
      }

      const criada = await addDoc(collection(db, caminho()), {
        householdId: HOUSEHOLD_ID,
        key,
        label,
        decision: decisionParaFirestore(decision),
        origin,
        scope,
        appliedCount: 0,
        lastAppliedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return criada.id
    },

    remove: async (id) => {
      await deleteDoc(doc(db, caminho(), id))
    },
  }
}

/**
 * `{ kind: 'category', categoryId }` guardado como está.
 *
 * Um objeto, e não dois campos (`categoryId` nulo querendo dizer "nunca"):
 * `null` já quer dizer "sem categoria" em toda a base, e usá-lo para "nunca
 * categorize" daria dois sentidos ao mesmo vazio.
 */
function decisionParaFirestore(decision: RuleDecision): DocumentData {
  return decision.kind === 'category'
    ? { kind: 'category', categoryId: decision.categoryId }
    : { kind: 'never' }
}

function regraDe(id: string, data: DocumentData): CategoryRule {
  const onde = `categoryRules/${id}`
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    key: requireText(data['key'], `${onde}.key`),
    label: textOr(data['label'], requireText(data['key'], `${onde}.key`)),
    decision: decisionDe(data['decision']),
    origin: data['origin'] === 'manual' ? 'manual' : 'learned',
    scope: escopoDe(data['scope']),
    appliedCount: typeof data['appliedCount'] === 'number' ? data['appliedCount'] : 0,
    lastAppliedAt: toIso(data['lastAppliedAt']),
    createdAt: toIso(data['createdAt']) ?? '',
    updatedAt: toIso(data['updatedAt']) ?? '',
  }
}

/**
 * Decisão irreconhecível vira `never`, e não "sem categoria".
 *
 * É o lado seguro: o pior que acontece é uma regra parar de categorizar e a
 * transação ficar amarela, esperando gente. O contrário — cair num `category`
 * inventado — categorizaria errado em silêncio.
 */
function decisionDe(value: unknown): RuleDecision {
  if (typeof value !== 'object' || value === null) return { kind: 'never' }
  const v = value as Record<string, unknown>
  const categoryId = optionalText(v['categoryId'])
  if (v['kind'] === 'category' && categoryId !== null) return { kind: 'category', categoryId }
  return { kind: 'never' }
}

function escopoDe(value: unknown): RuleScope {
  return value === 'item' || value === 'both' ? value : 'transaction'
}
